<script>
import VueApexCharts from 'vue-apexcharts'

import { apiSecond } from '@/boot/axios'
import { loadFile } from '@/helpers'

import Fish from '@/components/components/Inputs/fish'
import Region from '@/components/components/Inputs/region'
import AqEmpty from '@/components/aqua/AqEmpty.vue'
import AqKpi from '@/components/aqua/AqKpi.vue'
import AqPageHeader from '@/components/aqua/AqPageHeader.vue'
import AquaSelect from '@/views/account/reporting/aqua/AquaSelect.vue'

const COLORS = { primary: '#157FEE', success: '#0F8A4F', neutral: '#A9B8CC', warning: '#E39A1B' }
const CHART_FONT = 'Geologica, sans-serif'
const AXIS_COLOR = '#66768C'
const GRID_COLOR = '#EEF2F7'
const ROW_HEIGHT = 38
const TOP_LIMIT = 10
const LOW_CAPACITY_PERCENT = 50
const EXPORT_FORMATS = ['pdf', 'xlsx', 'docx']
const EXPORT_TYPES = {
	pdf: 'application/pdf',
	xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
}

export default {
	name: 'AquaAnalytics',
	components: {
		VueApexCharts,
		Fish,
		Region,
		AqEmpty,
		AqKpi,
		AqPageHeader,
		AquaSelect,
	},
	data() {
		return {
			analytics: null,
			loading: false,
			requestId: 0,
			availableRegions: null,
			exporting: null,
			exportFormats: EXPORT_FORMATS,
			activityTypes: [],
			lowCapacityPercent: LOW_CAPACITY_PERCENT,
			subjectQuery: '',
			subjectOptions: { page: 1, itemsPerPage: 10, sortBy: ['capacity'], sortDesc: [true] },
			filters: {
				report_year: new Date().getFullYear() - 1,
				region_id: null,
				fish_id: null,
				activity_type_id: null,
			},
		}
	},
	computed: {
		years() {
			const current = new Date().getFullYear()
			return Array.from({ length: 7 }, (_, index) => current - index)
		},
		yearOptions() {
			return this.years.map((year) => ({ value: year, text: year }))
		},
		activityOptions() {
			return [
				{ value: null, text: this.$t('aqua.analytics.all_activities') },
				...this.activityTypes.map((type) => ({ value: type.id, text: this.activityTypeLabel(type) })),
			]
		},
		hasFilters() {
			return Boolean(this.filters.region_id || this.filters.fish_id || this.filters.activity_type_id)
		},
		capacity() {
			return this.analytics.capacity
		},
		production() {
			return this.analytics.production
		},
		development() {
			return this.analytics.development
		},
		capacityByRegion() {
			return this.capacity.by_region.slice(0, TOP_LIMIT)
		},
		capacityByFish() {
			return this.capacity.by_fish.slice(0, TOP_LIMIT)
		},
		capacityRegionChart() {
			return this.capacityChart(this.capacityByRegion, this.$t('aqua.analytics.no_region'))
		},
		capacityFishChart() {
			return this.capacityChart(this.capacityByFish, '—')
		},
		regionConfines() {
			return this.availableRegions ? this.availableRegions.map((id) => ({ id })) : []
		},
		developmentRows() {
			const rows = this.development.by_chapter.map((row) => ({
				...row,
				title: this.$t(`aqua.analytics.chapters.${row.id}`),
			}))
			rows.push({ ...this.development.totals, id: 'total', title: this.$t('aqua.analytics.total') })
			if (this.development.previous_totals) {
				rows.push({
					...this.development.previous_totals,
					id: 'previous',
					title: this.$t('aqua.analytics.total_previous', { year: this.analytics.year - 1 }),
				})
			}
			return rows
		},
		developmentMetrics() {
			return ['fish', 'mto', 'construction', 'stocking'].map((key) => ({ key, title: this.$t(`aqua.analytics.${key}_plan_fact`) }))
		},
		monthsChart() {
			const months = Array.from({ length: 12 }, (_, index) =>
				new Date(2000, index, 1).toLocaleString(this.$i18n.locale === 'kz' ? 'kk-KZ' : 'ru-RU', { month: 'short' }),
			)
			return {
				series: [
					{ name: String(this.analytics.year), data: this.analytics.written_off_by_month.current },
					{ name: String(this.analytics.year - 1), data: this.analytics.written_off_by_month.previous },
				],
				options: this.chartOptions({
					chart: { type: 'bar' },
					colors: [COLORS.primary, COLORS.neutral],
					xaxis: { categories: months },
					plotOptions: { bar: { columnWidth: '58%', borderRadius: 4 } },
				}),
			}
		},
		productionChart() {
			const rows = this.production.by_region
			return {
				height: Math.max(220, rows.length * ROW_HEIGHT * 1.5),
				series: [
					{ name: this.$t('aqua.analytics.planned'), data: rows.map((row) => row.planned) },
					{ name: this.$t('aqua.analytics.actual'), data: rows.map((row) => row.actual) },
				],
				options: this.chartOptions({
					chart: { type: 'bar' },
					colors: [COLORS.neutral, COLORS.primary],
					plotOptions: { bar: { horizontal: true, barHeight: '70%', borderRadius: 4 } },
					xaxis: { categories: rows.map((row) => this.label(row, this.$t('aqua.analytics.no_region'))) },
				}),
			}
		},
		dynamicsChart() {
			const rows = this.analytics.dynamics
			return {
				series: [
					{ name: this.$t('aqua.analytics.planned'), data: rows.map((row) => row.planned) },
					{ name: this.$t('aqua.analytics.actual'), data: rows.map((row) => row.actual) },
					{ name: this.$t('aqua.analytics.sold'), data: rows.map((row) => row.sold) },
				],
				options: this.chartOptions({
					chart: { type: 'line' },
					colors: [COLORS.neutral, COLORS.primary, COLORS.success],
					stroke: { width: [2, 3, 3], curve: 'smooth', dashArray: [6, 0, 0] },
					markers: { size: 4, strokeWidth: 2, strokeColors: '#fff', hover: { size: 6 } },
					xaxis: { categories: rows.map((row) => String(row.year)) },
				}),
			}
		},
		subjects() {
			return this.analytics.subjects
		},
		subjectRows() {
			const query = this.subjectQuery.trim().toLowerCase()
			if (!query) return this.subjects.rows
			return this.subjects.rows.filter((row) => `${row.title || ''} ${row.bin}`.toLowerCase().includes(query))
		},
		subjectHeaders() {
			return [
				{ text: this.$t('aqua.ui.analytics.subjects_subject'), value: 'title' },
				{ text: this.$t('aqua.analytics.zpm_count'), value: 'zpm_count', align: 'end' },
				{ text: this.$t('aqua.analytics.capacity'), value: 'capacity', align: 'end' },
				{ text: this.$t('aqua.ui.analytics.subjects_capacity_percent'), value: 'percent', width: 130 },
				{ text: this.$t('aqua.ui.analytics.subjects_plan'), value: 'plan_percent', width: 150 },
				{ text: this.$t('aqua.analytics.sold'), value: 'sold', align: 'end' },
				{ text: this.$t('aqua.ui.analytics.subjects_development'), value: 'development_percent', width: 130 },
			]
		},
		hasMonths() {
			const { current, previous } = this.analytics.written_off_by_month
			return current.concat(previous).some((value) => value > 0)
		},
		hasDynamics() {
			return this.analytics.dynamics.some((row) => row.planned > 0 || row.actual > 0 || row.sold > 0)
		},
	},
	watch: {
		subjectQuery() {
			this.subjectOptions = { ...this.subjectOptions, page: 1 }
		},
		filters: {
			deep: true,
			handler() {
				this.fetch()
			},
		},
	},
	mounted() {
		this.fetch()
		this.loadActivityTypes()
	},
	methods: {
		params() {
			const params = { report_year: this.filters.report_year }
			;['region_id', 'fish_id', 'activity_type_id'].forEach((key) => {
				if (this.filters[key]) params[key] = this.filters[key]
			})
			return params
		},
		resetFilters() {
			this.filters = { ...this.filters, region_id: null, fish_id: null, activity_type_id: null }
		},
		async fetch() {
			const requestId = ++this.requestId
			this.loading = true
			try {
				const { data } = await apiSecond.get('v1/aqua/analytics', { params: this.params() })
				if (requestId === this.requestId) {
					this.analytics = data
					this.availableRegions = data.available_regions
					this.subjectOptions = { ...this.subjectOptions, page: 1 }
				}
			} catch (error) {
				if (requestId === this.requestId) {
					this.analytics = null
					this.handleError(error)
				}
			} finally {
				if (requestId === this.requestId) this.loading = false
			}
		},
		async loadActivityTypes() {
			try {
				const { data } = await apiSecond.get('v1/zpm/activity-types')
				this.activityTypes = data.data || []
			} catch (error) {
				this.handleError(error)
			}
		},
		async exportFile(format) {
			if (this.exporting) return
			this.exporting = format
			try {
				const response = await apiSecond.get(`v1/aqua/analytics/export/${format}`, {
					params: this.params(),
					responseType: 'blob',
				})
				loadFile(response.data, EXPORT_TYPES[format], `aqua-analytics-${this.filters.report_year}`, format)
			} catch (error) {
				this.handleError(await this.parseBlobError(error))
			} finally {
				this.exporting = null
			}
		},
		async parseBlobError(error) {
			const data = error?.response?.data
			if (!(data instanceof Blob)) return error
			try {
				error.response.data = JSON.parse(await data.text())
			} catch (parseError) {
				error.response.data = {}
			}
			return error
		},
		capacityChart(rows, untitled) {
			return {
				height: Math.max(200, rows.length * ROW_HEIGHT + 70),
				series: [
					{ name: this.$t('aqua.analytics.written_off'), data: rows.map((row) => row.written_off) },
					{ name: this.$t('aqua.analytics.remainder'), data: rows.map((row) => Math.max(row.remainder, 0)) },
				],
				options: this.chartOptions({
					chart: { type: 'bar', stacked: true },
					colors: [COLORS.primary, COLORS.neutral],
					plotOptions: { bar: { horizontal: true, barHeight: '58%', borderRadius: 4 } },
					xaxis: { categories: rows.map((row) => this.label(row, untitled)) },
				}),
			}
		},
		chartOptions(extra) {
			const formatter = (value) => (typeof value === 'number' ? this.number(value) : value)
			const axisLabels = { style: { colors: AXIS_COLOR, fontSize: '12px' } }
			return {
				...extra,
				chart: { fontFamily: CHART_FONT, foreColor: AXIS_COLOR, toolbar: { show: false }, zoom: { enabled: false }, ...extra.chart },
				dataLabels: { enabled: false },
				legend: {
					position: 'top',
					horizontalAlign: 'left',
					fontSize: '13px',
					labels: { colors: '#151D48' },
					markers: { width: 10, height: 10, radius: 3 },
					itemMargin: { horizontal: 10, vertical: 4 },
				},
				grid: { borderColor: GRID_COLOR, strokeDashArray: 4, padding: { left: 8, right: 16 } },
				states: { hover: { filter: { type: 'darken', value: 0.9 } }, active: { filter: { type: 'none' } } },
				tooltip: { theme: 'light', y: { formatter: (value) => this.number(value) } },
				xaxis: {
					...extra.xaxis,
					axisBorder: { color: GRID_COLOR },
					axisTicks: { show: false },
					labels: { ...axisLabels, formatter },
				},
				yaxis: { labels: { ...axisLabels, maxWidth: 220, formatter } },
			}
		},
		label(row, fallback) {
			const title = this.$i18n.locale === 'kz' && row.title_kz ? row.title_kz : row.title
			return title || fallback
		},
		topNote(shown, total) {
			return total > shown ? this.$t('aqua.analytics.top', { shown, total }) : ''
		},
		number(value) {
			return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value || 0)
		},
		percent(value) {
			return value === null || value === undefined ? '—' : `${String(value).replace('.', ',')}%`
		},
		planTone(value) {
			if (value === null || value === undefined) return 'neutral'
			if (value >= 100) return 'success'
			return value < 80 ? 'warning' : 'neutral'
		},
		meterWidth(value) {
			return `${Math.max(0, Math.min(100, Number(value) || 0))}%`
		},
		subjectMeta(row) {
			const region = this.$i18n.locale === 'kz' && row.region_title_kz ? row.region_title_kz : row.region_title
			const activity = this.$i18n.locale === 'kz' && row.activity_kz ? row.activity_kz : row.activity
			return [row.bin, region || this.$t('aqua.analytics.no_region'), activity].filter(Boolean).join(' · ')
		},
		capacityTone(value) {
			if (value === null || value === undefined) return 'neutral'
			return value < LOW_CAPACITY_PERCENT ? 'warning' : 'neutral'
		},
		activityTypeLabel(type) {
			return this.$i18n.locale === 'kz' && type.title_kz ? type.title_kz : type.title
		},
		handleError(error) {
			if (error?.response?.status === 401) {
				this.$router.push(`/${this.$i18n.locale}/login`)
				return
			}
			this.$toast.error(error?.response?.data?.message || this.$t('system_message.500'))
		},
	},
	beforeCreate() {
		if (!localStorage.token) this.$router.push('/ru/login')
	},
}
</script>

<template>
	<div class="wrap__content aq-page">
		<div class="container">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="['AquaAnalytics']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content aq-shell">
							<AqPageHeader
								:title="$t('aqua.analytics.title')"
								:eyebrow="$t('aqua.ui.analytics.eyebrow')"
								:subtitle="$t('aqua.analytics.subtitle')"
							>
								<template #actions>
									<div class="an-export" role="group" :aria-label="$t('aqua.ui.analytics.export')">
										<span class="an-export__label">{{ $t('aqua.ui.analytics.export') }}</span>
										<button
											v-for="format in exportFormats"
											:key="format"
											type="button"
											class="an-export__btn"
											:class="{ 'is-busy': exporting === format }"
											:disabled="Boolean(exporting)"
											:aria-label="$t('aqua.ui.analytics.export_aria', { format: format.toUpperCase() })"
											@click="exportFile(format)"
										>
											<span v-if="exporting === format" class="an-spinner" aria-hidden="true"></span>
											<svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
												<path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
											</svg>
											{{ format.toUpperCase() }}
										</button>
									</div>
								</template>
							</AqPageHeader>

							<section class="aq-card an-toolbar">
								<div class="an-filters">
									<AquaSelect v-model="filters.report_year" class="an-filter" :label="$t('aqua.ui.analytics.year')" :options="yearOptions" />
									<div class="an-filter">
										<span class="an-filter__label">{{ $t('aqua.ui.analytics.region') }}</span>
										<region
											:modelValue="filters.region_id"
											:confinesValue="regionConfines"
											:placeholder="$t('inputs.region')"
											@update:modelValue="filters.region_id = $event"
										></region>
									</div>
									<div class="an-filter">
										<span class="an-filter__label">{{ $t('aqua.ui.analytics.fish') }}</span>
										<Fish
											:multiple="false"
											name="fish"
											:modelValue="filters.fish_id"
											:placeholder="$t('filters.select_fish')"
											@update:modelValue="filters.fish_id = $event"
										/>
									</div>
									<AquaSelect
										v-model="filters.activity_type_id"
										class="an-filter"
										:label="$t('aqua.ui.analytics.activity')"
										:options="activityOptions"
									/>
									<button v-if="hasFilters" type="button" class="aq-btn aq-btn--ghost an-filters__reset" @click="resetFilters">
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
											<path d="M6 6l12 12M18 6L6 18" />
										</svg>
										{{ $t('aqua.ui.analytics.reset') }}
									</button>
								</div>
								<p class="an-hint">
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
										<circle cx="12" cy="12" r="9" />
										<path d="M12 11v5M12 8v.5" />
									</svg>
									{{ $t('aqua.analytics.hint') }}
								</p>
							</section>

							<div v-if="!analytics && loading" class="an-skeleton" aria-busy="true">
								<div class="an-kpis">
									<span v-for="index in 4" :key="`kpi-${index}`" class="an-skeleton__block an-skeleton__block--kpi"></span>
								</div>
								<div class="an-grid">
									<span v-for="index in 2" :key="`chart-${index}`" class="an-skeleton__block an-skeleton__block--chart"></span>
								</div>
							</div>

							<div v-else-if="!analytics" class="aq-card">
								<AqEmpty tone="danger" :title="$t('aqua.ui.analytics.load_failed')" :text="$t('aqua.ui.analytics.load_failed_text')">
									<button type="button" class="aq-btn aq-btn--primary" @click="fetch">{{ $t('aqua.ui.analytics.retry') }}</button>
								</AqEmpty>
							</div>

							<div v-else class="an-content" :class="{ 'is-loading': loading }" :aria-busy="String(loading)">
								<v-progress-linear v-if="loading" indeterminate color="primary" height="3" class="an-progress"></v-progress-linear>

								<section class="an-section">
									<header class="an-section__head">
										<h2 class="an-section__title">{{ $t('aqua.analytics.capacity_title') }}</h2>
										<p class="an-section__subtitle">{{ $t('aqua.ui.analytics.capacity_hint') }}</p>
									</header>
									<div class="an-kpis">
										<AqKpi
											:label="$t('aqua.analytics.zpm_count')"
											:value="number(capacity.totals.zpm_count)"
											:hint="`${$t('aqua.analytics.subjects')}: ${number(capacity.totals.subjects)}`"
										/>
										<AqKpi :label="$t('aqua.analytics.capacity')" :value="number(capacity.totals.capacity)" />
										<AqKpi
											:label="$t('aqua.analytics.written_off')"
											:value="number(capacity.totals.written_off)"
											:progress="capacity.totals.percent || 0"
											:hint="$t('aqua.ui.analytics.of_capacity', { percent: percent(capacity.totals.percent) })"
										/>
										<AqKpi tone="success" :label="$t('aqua.analytics.remainder')" :value="number(capacity.totals.remainder)" />
									</div>

									<div v-if="capacity.totals.zpm_count" class="an-grid">
										<article class="aq-card an-card">
											<header class="aq-card__head">
												<div>
													<h3 class="aq-card__title">{{ $t('aqua.analytics.by_region') }}</h3>
													<p v-if="topNote(capacityByRegion.length, capacity.by_region.length)" class="aq-card__subtitle">
														{{ topNote(capacityByRegion.length, capacity.by_region.length) }}
													</p>
												</div>
											</header>
											<div class="an-card__chart">
												<VueApexCharts
													type="bar"
													:height="capacityRegionChart.height"
													:options="capacityRegionChart.options"
													:series="capacityRegionChart.series"
												></VueApexCharts>
											</div>
										</article>
										<article class="aq-card an-card">
											<header class="aq-card__head">
												<div>
													<h3 class="aq-card__title">{{ $t('aqua.analytics.by_fish') }}</h3>
													<p v-if="topNote(capacityByFish.length, capacity.by_fish.length)" class="aq-card__subtitle">
														{{ topNote(capacityByFish.length, capacity.by_fish.length) }}
													</p>
												</div>
											</header>
											<div class="an-card__chart">
												<VueApexCharts
													type="bar"
													:height="capacityFishChart.height"
													:options="capacityFishChart.options"
													:series="capacityFishChart.series"
												></VueApexCharts>
											</div>
										</article>
										<article class="aq-card an-card">
											<header class="aq-card__head">
												<h3 class="aq-card__title">{{ $t('aqua.analytics.by_month', { year: analytics.year }) }}</h3>
											</header>
											<div class="an-card__chart">
												<VueApexCharts
													v-if="hasMonths"
													type="bar"
													:height="280"
													:options="monthsChart.options"
													:series="monthsChart.series"
												></VueApexCharts>
												<AqEmpty v-else :title="$t('aqua.analytics.empty')" :text="$t('aqua.ui.analytics.empty_hint')" />
											</div>
										</article>
										<article class="aq-card an-card">
											<header class="aq-card__head">
												<h3 class="aq-card__title">{{ $t('aqua.analytics.by_activity') }}</h3>
											</header>
											<div class="an-table-wrap">
												<table class="an-table an-table--compact">
													<thead>
														<tr>
															<th>{{ $t('aqua.analytics.activity') }}</th>
															<th class="num">{{ $t('aqua.analytics.zpm_count') }}</th>
															<th class="num">{{ $t('aqua.analytics.capacity') }}</th>
															<th class="num">{{ $t('aqua.analytics.written_off') }}</th>
															<th class="an-table__meter-col">%</th>
														</tr>
													</thead>
													<tbody>
														<tr v-for="row in capacity.by_activity" :key="`activity-${row.id}`">
															<td>{{ label(row, $t('aqua.analytics.no_activity')) }}</td>
															<td class="num">{{ row.zpm_count }}</td>
															<td class="num">{{ number(row.capacity) }}</td>
															<td class="num">{{ number(row.written_off) }}</td>
															<td>
																<span class="an-meter">
																	<span class="an-meter__track"><span :style="{ width: meterWidth(row.percent) }"></span></span>
																	<span class="an-meter__value">{{ percent(row.percent) }}</span>
																</span>
															</td>
														</tr>
													</tbody>
												</table>
											</div>
										</article>
									</div>
									<div v-else class="aq-card">
										<AqEmpty :title="$t('aqua.analytics.empty')" :text="$t('aqua.ui.analytics.empty_hint')" />
									</div>
								</section>

								<section class="an-section">
									<header class="an-section__head">
										<h2 class="an-section__title">{{ $t('aqua.analytics.production_title', { year: analytics.year }) }}</h2>
										<p class="an-section__subtitle">{{ $t('aqua.ui.analytics.production_hint') }}</p>
									</header>
									<div class="an-kpis">
										<AqKpi :label="$t('aqua.analytics.subjects')" :value="number(production.totals.subjects)" />
										<AqKpi :label="$t('aqua.analytics.planned')" :value="number(production.totals.planned)" />
										<AqKpi
											:label="$t('aqua.analytics.actual')"
											:value="number(production.totals.actual)"
											:tone="planTone(production.totals.percent)"
											:progress="production.totals.percent || 0"
											:hint="`${$t('aqua.analytics.plan_percent')}: ${percent(production.totals.percent)}`"
										/>
										<AqKpi
											tone="success"
											:label="$t('aqua.analytics.sold')"
											:value="number(production.totals.sold)"
											:hint="`${number(production.totals.sold_amount)} ${$t('aqua.analytics.thousand_tenge')}`"
										/>
									</div>
									<div class="an-grid">
										<article class="aq-card an-card">
											<header class="aq-card__head">
												<h3 class="aq-card__title">{{ $t('aqua.analytics.plan_by_region') }}</h3>
											</header>
											<div class="an-card__chart">
												<VueApexCharts
													v-if="production.by_region.length"
													type="bar"
													:height="productionChart.height"
													:options="productionChart.options"
													:series="productionChart.series"
												></VueApexCharts>
												<AqEmpty v-else :title="$t('aqua.analytics.empty')" :text="$t('aqua.ui.analytics.empty_hint')" />
											</div>
										</article>
										<article class="aq-card an-card">
											<header class="aq-card__head">
												<h3 class="aq-card__title">{{ $t('aqua.analytics.dynamics') }}</h3>
											</header>
											<div class="an-card__chart">
												<VueApexCharts
													v-if="hasDynamics"
													type="line"
													:height="280"
													:options="dynamicsChart.options"
													:series="dynamicsChart.series"
												></VueApexCharts>
												<AqEmpty v-else :title="$t('aqua.analytics.empty')" :text="$t('aqua.ui.analytics.empty_hint')" />
											</div>
										</article>
									</div>
									<article class="aq-card">
										<header class="aq-card__head">
											<h3 class="aq-card__title">{{ $t('aqua.analytics.production_by_fish') }}</h3>
										</header>
										<div v-if="production.by_fish.length" class="an-table-wrap">
											<table class="an-table">
												<thead>
													<tr>
														<th>{{ $t('aqua.analytics.fish') }}</th>
														<th class="num">{{ $t('aqua.analytics.subjects') }}</th>
														<th class="num">{{ $t('aqua.analytics.planned') }}</th>
														<th class="num">{{ $t('aqua.analytics.actual') }}</th>
														<th class="an-table__meter-col">{{ $t('aqua.analytics.plan_percent') }}</th>
														<th class="num">{{ $t('aqua.analytics.sold') }}</th>
													</tr>
												</thead>
												<tbody>
													<tr v-for="row in production.by_fish" :key="`fish-${row.id}`">
														<td>{{ label(row, '—') }}</td>
														<td class="num">{{ row.subjects }}</td>
														<td class="num">{{ number(row.planned) }}</td>
														<td class="num">{{ number(row.actual) }}</td>
														<td>
															<span class="an-meter" :class="`an-meter--${planTone(row.percent)}`">
																<span class="an-meter__track"><span :style="{ width: meterWidth(row.percent) }"></span></span>
																<span class="an-meter__value">{{ percent(row.percent) }}</span>
															</span>
														</td>
														<td class="num">{{ number(row.sold) }}</td>
													</tr>
												</tbody>
											</table>
										</div>
										<AqEmpty v-else :title="$t('aqua.analytics.empty')" :text="$t('aqua.ui.analytics.empty_hint')" />
									</article>
								</section>

								<section class="an-section">
									<header class="an-section__head">
										<h2 class="an-section__title">{{ $t('aqua.analytics.development_title', { year: analytics.year }) }}</h2>
										<p class="an-section__subtitle">{{ $t('aqua.ui.analytics.development_hint') }}</p>
									</header>
									<article class="aq-card">
										<div v-if="development.totals" class="an-table-wrap">
											<table class="an-table an-table--development">
												<thead>
													<tr>
														<th>{{ $t('aqua.analytics.chapter') }}</th>
														<th class="num">{{ $t('aqua.analytics.subjects') }}</th>
														<th v-for="metric in developmentMetrics" :key="metric.key">{{ metric.title }}</th>
													</tr>
												</thead>
												<tbody>
													<tr
														v-for="row in developmentRows"
														:key="`chapter-${row.id}`"
														:class="{ 'an-table__total': row.id === 'total', 'an-table__previous': row.id === 'previous' }"
													>
														<td>{{ row.title }}</td>
														<td class="num">{{ row.subjects }}</td>
														<td v-for="metric in developmentMetrics" :key="metric.key">
															<span class="an-plan">
																<span class="an-plan__values">
																	{{ number(row[`${metric.key}_plan`]) }} / <strong>{{ number(row[`${metric.key}_fact`]) }}</strong>
																</span>
																<span class="an-meter" :class="`an-meter--${planTone(row[`${metric.key}_percent`])}`">
																	<span class="an-meter__track">
																		<span :style="{ width: meterWidth(row[`${metric.key}_percent`]) }"></span>
																	</span>
																	<span class="an-meter__value">{{ percent(row[`${metric.key}_percent`]) }}</span>
																</span>
															</span>
														</td>
													</tr>
												</tbody>
											</table>
										</div>
										<AqEmpty v-else :title="$t('aqua.analytics.empty')" :text="$t('aqua.ui.analytics.empty_hint')" />
									</article>
								</section>

								<section class="an-section">
									<header class="an-section__head">
										<h2 class="an-section__title">{{ $t('aqua.ui.analytics.subjects_title') }}</h2>
										<p class="an-section__subtitle">{{ $t('aqua.ui.analytics.subjects_hint', { year: analytics.year }) }}</p>
									</header>
									<div class="an-kpis">
										<AqKpi :label="$t('aqua.ui.analytics.subjects_total')" :value="number(subjects.totals.subjects)" />
										<AqKpi :label="$t('aqua.ui.analytics.subjects_with_zpm')" :value="number(subjects.totals.with_zpm)" />
										<AqKpi
											tone="success"
											:label="$t('aqua.ui.analytics.subjects_reported', { year: analytics.year })"
											:value="number(subjects.totals.reported)"
										/>
										<AqKpi
											:tone="subjects.totals.low_capacity ? 'warning' : 'neutral'"
											:label="$t('aqua.ui.analytics.subjects_low', { percent: lowCapacityPercent })"
											:value="number(subjects.totals.low_capacity)"
											:hint="$t('aqua.ui.analytics.subjects_low_hint', { percent: lowCapacityPercent })"
										/>
									</div>
									<article class="aq-card an-subjects">
										<div class="an-subjects__toolbar">
											<label class="an-search" for="an-subject-search">
												<span class="sr-only">{{ $t('aqua.ui.analytics.subjects_search') }}</span>
												<svg class="an-search__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
													<circle cx="11" cy="11" r="7" />
													<path d="M20 20l-3.5-3.5" />
												</svg>
												<input
													id="an-subject-search"
													v-model="subjectQuery"
													class="aq-input an-search__input"
													type="search"
													:placeholder="$t('aqua.ui.analytics.subjects_search')"
												/>
											</label>
										</div>
										<v-data-table
											class="an-dt"
											:headers="subjectHeaders"
											:items="subjectRows"
											item-key="bin"
											:options.sync="subjectOptions"
											:footer-props="{ 'items-per-page-options': [10, 25, 50] }"
										>
											<template v-slot:item.title="{ item }">
												<div class="an-subject">
													<strong class="an-subject__name">{{ item.title || '—' }}</strong>
													<span class="an-subject__meta">{{ subjectMeta(item) }}</span>
												</div>
											</template>
											<template v-slot:item.zpm_count="{ item }">
												<span v-if="item.zpm_count">{{ item.zpm_count }}</span>
												<span v-else class="an-muted">{{ $t('aqua.ui.analytics.subjects_no_zpm') }}</span>
											</template>
											<template v-slot:item.capacity="{ item }">{{ item.zpm_count ? number(item.capacity) : '—' }}</template>
											<template v-slot:item.percent="{ item }">
												<span v-if="item.zpm_count" class="an-meter" :class="`an-meter--${capacityTone(item.percent)}`">
													<span class="an-meter__track"><span :style="{ width: meterWidth(item.percent) }"></span></span>
													<span class="an-meter__value">{{ percent(item.percent) }}</span>
												</span>
												<span v-else class="an-muted">—</span>
											</template>
											<template v-slot:item.plan_percent="{ item }">
												<span v-if="item.reported" class="an-plan">
													<span class="an-plan__values">{{ number(item.planned) }} / <strong>{{ number(item.actual) }}</strong></span>
													<span class="an-meter" :class="`an-meter--${planTone(item.plan_percent)}`">
														<span class="an-meter__track"><span :style="{ width: meterWidth(item.plan_percent) }"></span></span>
														<span class="an-meter__value">{{ percent(item.plan_percent) }}</span>
													</span>
												</span>
												<span v-else class="an-muted">{{ $t('aqua.ui.analytics.subjects_not_reported') }}</span>
											</template>
											<template v-slot:item.sold="{ item }">
												<span v-if="item.reported" class="an-sold">
													{{ number(item.sold) }}
													<span class="an-subject__meta">{{ number(item.sold_amount) }} {{ $t('aqua.analytics.thousand_tenge') }}</span>
												</span>
												<span v-else class="an-muted">—</span>
											</template>
											<template v-slot:item.development_percent="{ item }">
												<span v-if="item.development_reported" class="an-meter" :class="`an-meter--${planTone(item.development_percent)}`">
													<span class="an-meter__track"><span :style="{ width: meterWidth(item.development_percent) }"></span></span>
													<span class="an-meter__value">{{ percent(item.development_percent) }}</span>
												</span>
												<span v-else class="an-muted">{{ $t('aqua.ui.analytics.subjects_not_reported') }}</span>
											</template>
											<template v-slot:no-data>
												<AqEmpty
													:title="$t('aqua.ui.analytics.subjects_empty')"
													:text="subjectQuery ? $t('aqua.ui.analytics.subjects_empty_search') : $t('aqua.ui.analytics.empty_hint')"
												/>
											</template>
										</v-data-table>
									</article>
								</section>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.aq-shell {
	background: var(--aq-bg);
}
.an-export {
	display: inline-flex;
	align-items: center;
	padding: 4px;
	border: 1px solid var(--aq-border-strong);
	border-radius: 12px;
	background: var(--aq-surface);
}
.an-export__label {
	padding: 0 10px 0 8px;
	color: var(--aq-muted);
	font-size: 13px;
	font-weight: 500;
}
.an-export__btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	min-height: 36px;
	padding: 6px 12px;
	border-radius: 8px;
	color: var(--aq-ink);
	font-size: 14px;
	font-weight: 600;
	transition: background 0.2s var(--aq-ease), color 0.2s var(--aq-ease);
}
.an-export__btn:hover {
	background: var(--aq-primary-soft);
	color: var(--aq-primary);
}
.an-export__btn:focus-visible {
	outline: none;
	box-shadow: 0 0 0 3px rgba(21, 127, 238, 0.25);
}
.an-export__btn[disabled] {
	cursor: default;
	opacity: 0.5;
}
.an-export__btn.is-busy {
	opacity: 1;
	color: var(--aq-primary);
}
.an-spinner {
	width: 14px;
	height: 14px;
	border: 2px solid currentColor;
	border-right-color: transparent;
	border-radius: 50%;
	animation: an-spin 0.8s linear infinite;
}
@keyframes an-spin {
	to {
		transform: rotate(360deg);
	}
}
.an-toolbar {
	padding: 18px 24px 14px;
}
.an-filters {
	display: grid;
	grid-template-columns: 140px repeat(3, minmax(180px, 1fr)) auto;
	align-items: end;
	gap: 14px 16px;
}
.an-filter {
	display: flex;
	flex-direction: column;
	gap: 6px;
	min-width: 0;
}
.an-filter__label,
.an-filters ::v-deep .aqua-select__label {
	color: var(--aq-ink-2);
	font-size: 13px;
	font-weight: 500;
}
.an-filters ::v-deep .aqua-select {
	width: 100%;
	gap: 6px;
}
.an-filters ::v-deep .aqua-select__control {
	height: 44px;
	border-color: var(--aq-border-strong);
	border-radius: 10px;
	transition: border-color 0.2s var(--aq-ease);
}
.an-filters ::v-deep .aqua-select__control:hover {
	border-color: var(--aq-primary);
}
.an-filters ::v-deep .input-wrapper {
	margin: 0;
}
.an-filters ::v-deep .multiselect,
.an-filters ::v-deep .multiselect__tags {
	min-height: 44px;
}
.an-filters ::v-deep .multiselect__tags {
	height: 44px;
}
.an-filters ::v-deep .multiselect__placeholder,
.an-filters ::v-deep .multiselect__single {
	margin-bottom: 0;
	padding-top: 0;
}
.an-filters ::v-deep .input-body .multiselect__tags {
	padding-top: 11px;
	padding-left: 14px;
	border: 1px solid var(--aq-border-strong) !important;
	border-radius: 10px;
}
.an-filters ::v-deep .input-body .multiselect__tags:hover {
	border-color: var(--aq-primary) !important;
}
.an-filters ::v-deep .multiselect__select {
	height: 42px;
}
.an-filters ::v-deep .multiselect__single,
.an-filters ::v-deep .multiselect__placeholder {
	overflow: hidden;
	font-size: 15px;
	white-space: nowrap;
	text-overflow: ellipsis;
}
.an-filters__reset {
	white-space: nowrap;
}
.an-hint {
	display: flex;
	gap: 8px;
	margin: 14px 0 0;
	padding-top: 12px;
	border-top: 1px solid var(--aq-border);
	color: var(--aq-muted);
	font-size: 13px;
	line-height: 1.5;
}
.an-hint svg {
	flex-shrink: 0;
	margin-top: 1px;
	color: var(--aq-primary);
}
.an-content {
	position: relative;
	transition: opacity 0.2s var(--aq-ease);
}
.an-content.is-loading {
	opacity: 0.6;
}
.an-progress {
	position: absolute;
	top: -12px;
	left: 0;
	z-index: 1;
	border-radius: 999px;
}
.an-section {
	margin-bottom: 28px;
}
.an-section__head {
	margin: 8px 0 14px;
}
.an-section__title {
	margin: 0;
	font-size: 20px;
	font-weight: 700;
	color: var(--aq-ink);
}
.an-section__subtitle {
	margin: 4px 0 0;
	color: var(--aq-muted);
	font-size: 13px;
}
.an-kpis {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 16px;
	margin-bottom: 20px;
}
.an-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 0 20px;
}
.an-card {
	display: flex;
	flex-direction: column;
	min-width: 0;
}
.an-card__chart {
	padding: 8px 12px 4px;
}
.an-table-wrap {
	overflow-x: auto;
}
.an-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 14px;
	font-variant-numeric: tabular-nums;
}
.an-table th {
	padding: 12px 16px;
	border-bottom: 1px solid var(--aq-border);
	background: #f8fafd;
	color: var(--aq-ink-2);
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.02em;
	text-align: left;
	vertical-align: bottom;
}
.an-table td {
	padding: 12px 16px;
	border-bottom: 1px solid var(--aq-border);
	color: var(--aq-ink);
	vertical-align: middle;
}
.an-table tbody tr {
	transition: background 0.15s var(--aq-ease);
}
.an-table tbody tr:hover {
	background: #f8fbff;
}
.an-table tbody tr:last-child td {
	border-bottom: 0;
}
.an-table .num {
	text-align: right;
	white-space: nowrap;
}
.an-table__meter-col {
	min-width: 150px;
}
.an-table--compact th,
.an-table--compact td {
	padding: 12px 10px;
}
.an-table--compact th:first-child,
.an-table--compact td:first-child {
	padding-left: 20px;
}
.an-table--compact .an-table__meter-col {
	min-width: 110px;
}
.an-table--compact .an-meter__track {
	min-width: 36px;
}
.an-table--compact .an-meter__value {
	min-width: 44px;
}
.an-table__total td {
	border-top: 2px solid var(--aq-border-strong);
	background: var(--aq-primary-soft);
	font-weight: 700;
}
.an-table__previous td {
	color: var(--aq-muted);
}
.an-table--development td {
	min-width: 150px;
}
.an-table--development td:first-child {
	min-width: 200px;
}
.an-plan {
	display: flex;
	flex-direction: column;
	gap: 6px;
}
.an-plan__values {
	white-space: nowrap;
}
.an-meter {
	display: flex;
	align-items: center;
	gap: 10px;
}
.an-meter__track {
	flex: 1 1 auto;
	min-width: 60px;
	height: 6px;
	border-radius: 999px;
	background: #edf2f8;
	overflow: hidden;
}
.an-meter__track span {
	display: block;
	height: 100%;
	border-radius: inherit;
	background: var(--aq-primary);
	transition: width 0.3s var(--aq-ease);
}
.an-meter__value {
	min-width: 52px;
	color: var(--aq-ink);
	font-size: 13px;
	font-weight: 600;
	text-align: right;
	white-space: nowrap;
}
.an-meter--success .an-meter__track span {
	background: var(--aq-success);
}
.an-meter--success .an-meter__value {
	color: var(--aq-success);
}
.an-meter--warning .an-meter__track span {
	background: #e39a1b;
}
.an-meter--warning .an-meter__value {
	color: var(--aq-warning);
}
.an-table__previous .an-meter__value {
	color: var(--aq-muted);
}
.an-subjects {
	overflow: hidden;
}
.an-subjects__toolbar {
	padding: 16px 24px;
	border-bottom: 1px solid var(--aq-border);
}
.an-search {
	position: relative;
	display: block;
	max-width: 420px;
	margin: 0;
}
.an-search__icon {
	position: absolute;
	top: 13px;
	left: 12px;
	color: var(--aq-muted);
	pointer-events: none;
}
.an-search__input {
	width: 100%;
	padding-left: 38px;
}
.an-subject {
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 180px;
	max-width: 240px;
	padding: 4px 0;
}
.an-subject__name {
	color: var(--aq-ink);
	font-size: 14px;
	font-weight: 600;
}
.an-subject__meta {
	display: block;
	color: var(--aq-muted);
	font-size: 12px;
	font-weight: 400;
	line-height: 1.4;
}
.an-sold {
	white-space: nowrap;
}
.an-muted {
	color: var(--aq-muted);
	font-size: 13px;
}
.an-dt ::v-deep table {
	font-variant-numeric: tabular-nums;
}
.an-dt ::v-deep thead th {
	height: 48px;
	background: #f8fafd;
	color: var(--aq-ink-2) !important;
	font-size: 12px !important;
	font-weight: 600;
	line-height: 1.3;
}
.an-dt ::v-deep tbody td {
	height: 64px;
	border-bottom: 1px solid var(--aq-border) !important;
	color: var(--aq-ink);
	font-size: 14px !important;
}
.an-dt ::v-deep thead th,
.an-dt ::v-deep tbody td {
	padding: 8px 10px !important;
}
.an-dt ::v-deep thead th:first-child,
.an-dt ::v-deep tbody td:first-child {
	padding-left: 24px !important;
}
.an-dt ::v-deep thead th:last-child,
.an-dt ::v-deep tbody td:last-child {
	padding-right: 24px !important;
}
.an-dt ::v-deep .text-end {
	text-align: right !important;
}
.an-dt ::v-deep thead th,
.an-dt ::v-deep thead th span {
	white-space: normal !important;
}
.an-dt .an-meter {
	gap: 8px;
}
.an-dt .an-meter__track {
	min-width: 40px;
}
.an-dt .an-meter__value {
	min-width: 46px;
}
.an-dt ::v-deep tbody tr:hover:not(.v-data-table__empty-wrapper) {
	background: #f8fbff !important;
}
.an-dt ::v-deep .v-data-table__empty-wrapper td {
	height: auto;
}
.an-dt ::v-deep .v-data-footer {
	padding: 6px 16px;
	border-top: 1px solid var(--aq-border) !important;
	color: var(--aq-muted);
	font-size: 13px;
}
.an-skeleton__block {
	display: block;
	border-radius: var(--aq-radius);
	background: linear-gradient(90deg, #edf2f8 0%, #f6f9fc 50%, #edf2f8 100%);
	background-size: 200% 100%;
	animation: an-shimmer 1.4s ease-in-out infinite;
}
.an-skeleton__block--kpi {
	height: 104px;
}
.an-skeleton__block--chart {
	height: 320px;
	margin-bottom: 20px;
}
@keyframes an-shimmer {
	from {
		background-position: 200% 0;
	}
	to {
		background-position: -200% 0;
	}
}
@media (max-width: 1360px) {
	.an-filters {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
	.an-grid {
		grid-template-columns: minmax(0, 1fr);
	}
}
@media (max-width: 1100px) {
	.an-kpis {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}
@media (max-width: 640px) {
	.an-filters,
	.an-kpis {
		grid-template-columns: minmax(0, 1fr);
	}
}
</style>
